import React from 'react';
import { Card, Button, Typography, Space, Form, Input, Divider, Alert, List, message } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import api from '../../../services/api';

const { Title, Paragraph, Text } = Typography;

function AdminSMTP() {
    const [health, setHealth] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [sending, setSending] = React.useState(false);

    const checkHealth = async () => {
        try {
            setLoading(true);
            const res = await api.adminSmtpHealth();
            setHealth(res);
        } catch (e) {
            // Handle authentication errors
            if (e.message && e.message.includes('Unauthenticated')) {
                message.error('Your session has expired. Please log in again.');
                setTimeout(() => {
                    window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
                }, 2000);
                return;
            }
            message.error(e.message || 'SMTP health check failed');
        } finally { setLoading(false); }
    };

    const onSend = async (values) => {
        try {
            setSending(true);
            const response = await api.adminSmtpSendTest(values);
            if (response && response.success) {
                message.success(response.message || 'SMTP test email sent successfully');
            } else {
                message.error(response?.message || 'Failed to send SMTP test');
            }
        } catch (e) {
            // Handle authentication errors
            if (e.message && e.message.includes('Unauthenticated')) {
                message.error('Your session has expired. Please log in again.');
                setTimeout(() => {
                    window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
                }, 2000);
                return;
            }

            const errorData = e.response?.data;
            if (errorData && errorData.suggestions) {
                message.error({
                    content: (
                        <div>
                            <div>{errorData.message}</div>
                            <div style={{ marginTop: 8 }}>
                                <Text strong>Suggestions:</Text>
                                <ul style={{ marginTop: 4, marginBottom: 0 }}>
                                    {errorData.suggestions.map((suggestion, index) => (
                                        <li key={index}>{suggestion}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ),
                    duration: 10
                });
            } else {
                message.error(e.message || 'Failed to send SMTP test');
            }
        } finally { setSending(false); }
    };

    return (
        <div>
            <div className="mb-6">
                <Title level={2}>Email (SMTP)</Title>
                <Paragraph type="secondary">Use your own SMTP. Run a health check and send a test email.</Paragraph>
            </div>

            <Card className="mb-6">
                <Title level={4}>Current SMTP Configuration</Title>
                <Paragraph type="secondary">
                    Configure your SMTP settings in the <Text code>.env</Text> file with these variables:
                </Paragraph>
                <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 4, fontFamily: 'monospace' }}>
                    <div>MAIL_MAILER=smtp</div>
                    <div>MAIL_HOST=your-smtp-server.com</div>
                    <div>MAIL_PORT=587</div>
                    <div>MAIL_USERNAME=your-email@domain.com</div>
                    <div>MAIL_PASSWORD=your-password</div>
                    <div>MAIL_ENCRYPTION=tls</div>
                    <div>MAIL_FROM_ADDRESS=noreply@yourdomain.com</div>
                    <div>MAIL_FROM_NAME="Your App Name"</div>
                </div>
                <Paragraph type="secondary" style={{ marginTop: 8 }}>
                    Common SMTP providers: Gmail (smtp.gmail.com:587), Outlook (smtp-mail.outlook.com:587), 
                    SendGrid (smtp.sendgrid.net:587), or your hosting provider's SMTP server.
                </Paragraph>
            </Card>

            <Card className="mb-6">
                <Space>
                    <Button type="primary" onClick={checkHealth} loading={loading}>Run Health Check</Button>
                </Space>
                <Divider />
                {health ? (
                    <div>
                        <Alert
                            message={health.shortSummary || health.label}
                            type={health.status === 'ok' ? 'success' : 'error'}
                            icon={health.status === 'ok' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                        
                        {health.meta && (
                            <div className="space-y-2">
                                <div><Text strong>Host:</Text> {health.meta.host}</div>
                                <div><Text strong>Port:</Text> {health.meta.port}</div>
                                <div><Text strong>Encryption:</Text> {health.meta.encryption || 'none'}</div>
                                <div><Text strong>Latency:</Text> {health.meta.latencyMs}ms</div>
                                {health.meta.greeting && <div><Text strong>Server Greeting:</Text> {health.meta.greeting}</div>}
                                {health.meta.error_code && (
                                    <div><Text strong>Error Code:</Text> {health.meta.error_code}</div>
                                )}
                                {health.meta.error_string && (
                                    <div><Text strong>Error Details:</Text> {health.meta.error_string}</div>
                                )}
                            </div>
                        )}
                        
                        {health.meta?.suggestions && health.status !== 'ok' && (
                            <div style={{ marginTop: 16 }}>
                                <Text strong>Troubleshooting Suggestions:</Text>
                                <List
                                    size="small"
                                    dataSource={health.meta.suggestions}
                                    renderItem={(item) => (
                                        <List.Item>
                                            <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
                                            {item}
                                        </List.Item>
                                    )}
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    <Text type="secondary">Click "Run Health Check" to test connectivity.</Text>
                )}
            </Card>

            <Card>
                <Title level={4}>Send Test Email</Title>
                <Form layout="vertical" onFinish={onSend} initialValues={{ subject: 'SMTP Test', body: 'SMTP test from admin panel' }}>
                    <Form.Item name="to" label="Recipient" rules={[{ required: true, message: 'Enter recipient email' }, { type: 'email', message: 'Enter a valid email' }]}>
                        <Input placeholder="you@example.com" />
                    </Form.Item>
                    <Form.Item name="subject" label="Subject">
                        <Input />
                    </Form.Item>
                    <Form.Item name="body" label="Message">
                        <Input.TextArea rows={4} />
                    </Form.Item>
                    <Space>
                        <Button type="primary" htmlType="submit" loading={sending}>Send Test</Button>
                        <Button onClick={checkHealth}>Refresh Health</Button>
                    </Space>
                </Form>
            </Card>
        </div>
    );
}

export default AdminSMTP;


